import Swal, { SweetAlertOptions } from 'sweetalert2'
import DOMPurify from 'dompurify'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { translateApiErrorMessage } from '@/utils/translateMessage'

type NotificationType = 'success' | 'error' | 'warning' | 'info'

export function useSwal() {
  const { t } = useTranslation()

  return useMemo(() => {
  const theme = {
    background: 'hsl(var(--popover))',
    color: 'hsl(var(--popover-foreground))',
    customClass: { popup: 'border border-border shadow-lg', title: 'break-words text-start', htmlContainer: 'text-start' },
  }
  // Non-blocking corner toast — the app-wide notification surface used by
  // useErrorHandler and the API error events (useSwal is the notification
  // system; sonner/notificationsSlice are not mounted).
  const notificationToast = Swal.mixin({
    ...theme,
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 5000,
    timerProgressBar: true,
    didOpen: (el) => {
      el.dir = document.documentElement.dir || 'ltr'
      el.addEventListener('mouseenter', Swal.stopTimer)
      el.addEventListener('mouseleave', Swal.resumeTimer)
    },
  })

  const notify = (type: NotificationType, message: string, duration?: number) => {
    const translated = message ? translateApiErrorMessage(message, t) : ''
    return notificationToast.fire({
      icon: type,
      title: translated.replace(/\n/g, ' — '),
      timer: duration ?? (type === 'error' ? 8000 : 5000),
    })
  }

  const defaults = {
    confirm: (): SweetAlertOptions => ({
      confirmButtonText: t('Confirm'),
      showCancelButton: false,
    }),
    delete: (): SweetAlertOptions => ({
      title: t('DeleteDialog.Title'),
      text: t('DeleteDialog.Text'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('DeleteDialog.confirmButtonText'),
      cancelButtonText: t('Cancel'),
    }),
    success: (title?: string): SweetAlertOptions => {
      const translated = title ? translateApiErrorMessage(title, t) : translateApiErrorMessage('Done', t)
      return {
        icon: 'success',
        title: translated,
        timer: 2000,
        showConfirmButton: false,
      }
    },
    error: (title?: string): SweetAlertOptions => {
      const translated = title ? translateApiErrorMessage(title, t) : translateApiErrorMessage('Error', t)
      return {
        icon: 'error',
        title: translated,
      }
    },
  }

  const fire = (options: SweetAlertOptions) => Swal.fire({ ...theme, ...options })

  // Radix disables pointer events on the body while an ERP dialog is open.
  // SweetAlert defaults to appending its modal to body, where the dialog's
  // body-level pointer lock makes the visible confirmation impossible to click.
  // Mount blocking prompts into the active dialog so they stay interactive.
  const blockingTarget = () =>
    typeof document === 'undefined'
      ? undefined
      : Array.from(document.querySelectorAll<HTMLElement>('[data-slot="dialog-content"][data-state="open"]')).filter(dialog => dialog.getAttribute('aria-hidden') !== 'true').at(-1) ?? document.body

  const fireBlocking = (options: SweetAlertOptions) =>
    fire({ ...options, target: blockingTarget() })

  const localizeOptions = (opts: Record<string, any>): SweetAlertOptions => {
    const localized: Record<string, any> = { ...opts }
    if (localized.title && typeof localized.title === 'string') {
      localized.title = translateApiErrorMessage(localized.title, t)
    }
    if (localized.text && typeof localized.text === 'string') {
      localized.text = translateApiErrorMessage(localized.text, t)
    }
    if (localized.confirmButtonText && typeof localized.confirmButtonText === 'string') {
      localized.confirmButtonText = translateApiErrorMessage(localized.confirmButtonText, t)
    }
    if (localized.cancelButtonText && typeof localized.cancelButtonText === 'string') {
      localized.cancelButtonText = translateApiErrorMessage(localized.cancelButtonText, t)
    }
    return localized as SweetAlertOptions
  }

  const confirm = (overrides: SweetAlertOptions = {}) =>
    fireBlocking(localizeOptions({ ...defaults.confirm(), ...overrides }))

  const deleteConfirm = (overrides: SweetAlertOptions = {}) =>
    fireBlocking(localizeOptions({ ...defaults.delete(), ...overrides }))

  const toast = (title: string, icon: SweetAlertOptions['icon'] = 'success') => {
    // defaults.success() already translates: pre-translating here translated the
    // message twice (and re-matched already-Arabic text against the patterns).
    return notificationToast.fire({ ...defaults.success(title), icon, toast: true })
  }

  const loading = (title?: string) => {
    const translated = title ? translateApiErrorMessage(title, t) : translateApiErrorMessage('Loading', t)
    return fire({
      title: translated,
      didOpen: () => Swal.showLoading(),
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
    })
  }

  const close = () => Swal.close()

  const deleteWithConfirm = async (
    overrides: SweetAlertOptions = {},
  ): Promise<boolean> => {
    // A rejected modal promise used to surface as an unhandled rejection,
    // because confirmDelete awaits this and ignores failures.
    try {
      const result = await fireBlocking(localizeOptions({ ...defaults.delete(), ...overrides }))
      return result.isConfirmed
    } catch {
      return false
    }
  }

  const confirmDelete = (text?: string): Promise<boolean> =>
    deleteWithConfirm(text ? { text: translateApiErrorMessage(text, t) } : {})

  const toastSuccess = (title: string) => toast(title, 'success')

  const toastWarning = (title: string) => toast(title, 'warning')

  const toastError = (title: string) => toast(title, 'error')

  const info = (title: string, html?: string, overrides: SweetAlertOptions = {}) => {
    const translatedTitle = title ? translateApiErrorMessage(title, t) : ''
    const translatedHtml = html ? translateApiErrorMessage(html, t) : html
    // Sanitize caller/remote-supplied markup: SweetAlert2's built-in
    // setInnerHtml only neutralizes <script>, inline handlers such as
    // <img onerror=...> still execute (XSS).
    const safeHtml = translatedHtml ? DOMPurify.sanitize(translatedHtml) : translatedHtml
    // Caller overrides must be localized like confirm/deleteConfirm do —
    // spreading them raw left a caller-supplied title/text untranslated. Only
    // the OVERRIDES go through localizeOptions: title/html above are already
    // translated and must not be translated a second time.
    const localizedOverrides = localizeOptions(overrides)
    return fireBlocking({ title: translatedTitle, html: safeHtml, icon: 'info', ...localizedOverrides })
  }

  return {
    fire,
    confirm,
    deleteConfirm,
    deleteWithConfirm,
    confirmDelete,
    toast,
    toastSuccess,
    toastWarning,
    toastError,
    notify,
    loading,
    info,
    close,
  }
  }, [t])
}
